(function () {
  'use strict';

  var STORAGE_KEY = 'wishlist_items';
  var SCHEMA_VERSION = 1;
  var _storageAvailable = null;
  var _cache = null;

  // ── Utilities ─────────────────────────────────────────────────────────────

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  function safeUrl(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return '/';
      return parsed.pathname + parsed.search;
    } catch (e) {
      return '/';
    }
  }

  function isStorageAvailable() {
    if (_storageAvailable !== null) return _storageAvailable;
    try {
      localStorage.setItem('__wl_test__', '1');
      localStorage.removeItem('__wl_test__');
      _storageAvailable = true;
    } catch (e) {
      _storageAvailable = false;
    }
    return _storageAvailable;
  }

  // ── Anonymous session ID ──────────────────────────────────────────────────

  var SESSION_ID_KEY = 'wishlist_sid';

  function getSessionId() {
    var sid = isStorageAvailable() ? localStorage.getItem(SESSION_ID_KEY) : null;
    if (sid) return sid;
    var arr = new Uint8Array(16);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(arr);
    } else {
      for (var i = 0; i < 16; i++) arr[i] = Math.floor(Math.random() * 256);
    }
    sid = Array.prototype.map.call(arr, function (b) {
      return ('0' + b.toString(16)).slice(-2);
    }).join('');
    if (isStorageAvailable()) localStorage.setItem(SESSION_ID_KEY, sid);
    return sid;
  }

  // ── Server-side event beacon ──────────────────────────────────────────────

  function beaconEvent(productId, action, productTitle, productSubtitle) {
    var url = window.__WL_BEACON__;
    if (!url) return;
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          action: action,
          session_id: getSessionId(),
          customer_id: window.__WL_CUSTOMER_ID__ != null ? String(window.__WL_CUSTOMER_ID__) : null,
          product_title: productTitle || '',
          product_subtitle: productSubtitle || ''
        }),
        keepalive: true
      }).catch(function () { /* fire-and-forget — ignore failures */ });
    } catch (e) { /* fetch blocked or unavailable — ignore */ }
  }

  // ── Wishlist data layer ───────────────────────────────────────────────────

  var Wishlist = {
    get: function () {
      if (_cache !== null) return _cache;
      if (!isStorageAvailable()) {
        _cache = [];
        return _cache;
      }
      try {
        var raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        // migrate v0 items (written before schema versioning was added)
        _cache = raw.map(function (item) {
          if (!item._v) {
            var migrated = { _v: SCHEMA_VERSION };
            var keys = Object.keys(item);
            for (var i = 0; i < keys.length; i++) migrated[keys[i]] = item[keys[i]];
            return migrated;
          }
          return item;
        });
        return _cache;
      } catch (e) {
        _cache = [];
        return _cache;
      }
    },

    save: function (items) {
      _cache = items;
      if (!isStorageAvailable()) return false;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        return true;
      } catch (e) {
        // QuotaExceededError or SecurityError — keep in-memory but flag failure
        _cache = items;
        return false;
      }
    },

    add: function (product) {
      var items = this.get();
      if (!items.find(function (i) { return i.id === product.id; })) {
        items.unshift(product);
        this.save(items);
      }
    },

    remove: function (productId) {
      var items = this.get().filter(function (i) { return i.id !== productId; });
      this.save(items);
    },

    has: function (productId) {
      return this.get().some(function (i) { return i.id === productId; });
    },

    count: function () {
      return this.get().length;
    }
  };

  // ── Header count badge ────────────────────────────────────────────────────

  function updateHeaderCount() {
    var bubble = document.getElementById('wishlist-count-bubble');
    if (!bubble) return;
    var count = Wishlist.count();
    if (count > 0) {
      var displayCount = count < 100 ? String(count) : '99+';
      var numSpan = bubble.querySelector('[data-wishlist-count-number]');
      var lblSpan = bubble.querySelector('[data-wishlist-count-label]');
      if (numSpan) numSpan.textContent = displayCount;
      if (lblSpan) lblSpan.textContent = count + ' פריטים ברשימת המשאלות';
      bubble.hidden = false;
    } else {
      bubble.hidden = true;
    }
  }

  // ── Button state ──────────────────────────────────────────────────────────

  function updateButton(btn, isActive) {
    btn.classList.toggle('wishlist-btn--active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    var addText = btn.dataset.addText || 'הוספה לרשימת המשאלות';
    var removeText = btn.dataset.removeText || 'הסרה מרשימת המשאלות';
    btn.setAttribute('aria-label', isActive ? removeText : addText);
    var label = btn.querySelector('.wishlist-btn__label');
    if (label) label.textContent = isActive ? removeText : addText;
  }

  function bindButton(btn) {
    var id = btn.dataset.productId;
    updateButton(btn, Wishlist.has(id));

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var isActive = Wishlist.has(id);
      var newState = !isActive;

      if (isActive) {
        Wishlist.remove(id);
      } else {
        Wishlist.add({
          _v: SCHEMA_VERSION,
          id: id,
          title: btn.dataset.productTitle || '',
          subtitle: btn.dataset.productSubtitle || '',
          handle: btn.dataset.productHandle || '',
          url: btn.dataset.productUrl || '',
          image: btn.dataset.productImage || '',
          price: btn.dataset.productPrice || '',
          compare_at_price: btn.dataset.productComparePrice || '',
          currency: btn.dataset.currency || ''
        });
        // Trigger pop animation only on add (not on page-load active state)
        btn.classList.add('wishlist-btn--pop');
        btn.addEventListener('animationend', function onPop() {
          btn.classList.remove('wishlist-btn--pop');
          btn.removeEventListener('animationend', onPop);
        });
      }

      updateButton(btn, newState);
      // Use known newState — avoids a redundant localStorage read
      document.querySelectorAll('.wishlist-btn[data-product-id="' + id + '"]').forEach(function (b) {
        if (b !== btn) updateButton(b, newState);
      });
      updateHeaderCount();
      // WISHLIST-START: server-side beacon
      beaconEvent(id, isActive ? 'remove' : 'add', btn.dataset.productTitle || '', btn.dataset.productSubtitle || '');
      // WISHLIST-END: server-side beacon
      // WISHLIST-START: Shopify analytics pixel event
      if (window.analytics) {
        try {
          analytics.publish('wishlist_changed', {
            action: isActive ? 'remove' : 'add',
            product_id: id,
            product_title: btn.dataset.productTitle || '',
            product_subtitle: btn.dataset.productSubtitle || ''
          });
        } catch (e) { /* analytics sandbox not ready — ignore */ }
      }
      // WISHLIST-END: Shopify analytics pixel event
      document.dispatchEvent(new CustomEvent('wishlist:changed', {
        detail: { id: id, action: isActive ? 'remove' : 'add' }
      }));
    });
  }

  function initButtons() {
    document.querySelectorAll('.wishlist-btn:not([data-wishlist-bound])').forEach(function (btn) {
      btn.dataset.wishlistBound = '1';
      bindButton(btn);
    });
  }

  // ── Wishlist page ─────────────────────────────────────────────────────────

  function renderWishlistPage() {
    var container = document.getElementById('wishlist-items-container');
    var empty = document.getElementById('wishlist-empty');
    if (!container) return;

    var items = Wishlist.get();
    var isHe = document.documentElement.getAttribute('lang') === 'he';
    var viewText = isHe ? 'לצפייה במוצר' : 'View Product';

    if (items.length === 0) {
      container.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;

    container.innerHTML = items.map(function (item) {
      var hasCompare = item.compare_at_price && item.compare_at_price !== item.price;
      var removeLabel = isHe
        ? 'הסרת ' + escapeHtml(item.title) + ' מרשימת המשאלות'
        : 'Remove ' + escapeHtml(item.title) + ' from wishlist';
      var hasHandle = !!item.handle;
      var modalId = 'QuickAdd-wl-' + escapeHtml(item.id);
      var quickAddUrl = hasHandle ? '/products/' + encodeURIComponent(item.handle) : '';
      var chooseText = 'בחרי מידה';

      return (
        '<div class="wishlist-item" data-product-id="' + escapeHtml(item.id) + '">' +
          '<a href="' + safeUrl(item.url) + '" class="wishlist-item__image-link">' +
            '<div class="wishlist-item__image-wrapper">' +
              (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '') +
            '</div>' +
          '</a>' +
          '<div class="wishlist-item__info">' +
            '<a href="' + safeUrl(item.url) + '" class="wishlist-item__title">' + escapeHtml(item.title) + '</a>' +
            '<div class="wishlist-item__price">' +
              (hasCompare ? '<span class="wishlist-item__price--compare">' + escapeHtml(item.compare_at_price) + '</span>' : '') +
              '<span class="wishlist-item__price--current">' + escapeHtml(item.price) + '</span>' +
            '</div>' +
            '<div class="wishlist-item__actions">' +
              (hasHandle
                ? (
                    '<modal-opener data-modal="#' + modalId + '">' +
                      '<button type="button" name="add" ' +
                        'class="button button--secondary wishlist-item__quick-add" ' +
                        'aria-haspopup="dialog" ' +
                        'data-product-url="' + quickAddUrl + '">' +
                        chooseText +
                        '<span class="loading__spinner hidden">' +
                          '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
                            '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
                          '</svg>' +
                        '</span>' +
                      '</button>' +
                    '</modal-opener>' +
                    '<quick-add-modal id="' + modalId + '" class="quick-add-modal">' +
                      '<div role="dialog" ' +
                        'aria-label="' + escapeHtml(item.title) + '" ' +
                        'aria-modal="true" ' +
                        'class="quick-add-modal__content global-settings-popup" ' +
                        'tabindex="-1">' +
                        '<button id="ModalClose-wl-' + escapeHtml(item.id) + '" type="button" class="quick-add-modal__toggle" aria-label="' + (isHe ? 'סגירה' : 'Close') + '">&#x2715;</button>' +
                        '<div id="QuickAddInfo-wl-' + escapeHtml(item.id) + '" class="quick-add-modal__content-info"></div>' +
                      '</div>' +
                    '</quick-add-modal>'
                  )
                : '<a href="' + safeUrl(item.url) + '" class="button button--secondary wishlist-item__view-btn">' + viewText + '</a>'
              ) +
              '<button class="wishlist-item__remove" data-remove-id="' + escapeHtml(item.id) + '" ' +
                'data-product-title="' + escapeHtml(item.title) + '" ' +
                'data-product-subtitle="' + escapeHtml(item.subtitle || '') + '" ' +
                'aria-label="' + removeLabel + '" type="button">&#215;</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    container.querySelectorAll('[data-remove-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = this.dataset.removeId;
        Wishlist.remove(id);
        // WISHLIST-START: server-side beacon
        beaconEvent(id, 'remove', this.dataset.productTitle || '', this.dataset.productSubtitle || '');
        // WISHLIST-END: server-side beacon
        // WISHLIST-START: Shopify analytics pixel event
        if (window.analytics) {
          try {
            analytics.publish('wishlist_changed', { action: 'remove', product_id: id, product_title: this.dataset.productTitle || '', product_subtitle: this.dataset.productSubtitle || '' });
          } catch (e) {}
        }
        // WISHLIST-END: Shopify analytics pixel event
        // Remove just this item from DOM — no full re-render
        var itemEl = container.querySelector('[data-product-id="' + id + '"]');
        if (itemEl) itemEl.remove();
        if (container.children.length === 0 && empty) empty.hidden = false;
        updateHeaderCount();
        document.querySelectorAll('.wishlist-btn[data-product-id="' + id + '"]').forEach(function (b) {
          updateButton(b, false);
        });
        document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { id: id, action: 'remove' } }));
      });
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    updateHeaderCount();
    initButtons();
    renderWishlistPage();
  });

  // Re-init buttons after dynamic content injection (quick-add modals, etc.)
  document.addEventListener('monica:wishlist:init-buttons', initButtons);

  // Re-init after Shopify theme editor section reloads
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      _cache = null;
      initButtons();
      updateHeaderCount();
    });
  }

  // Cross-tab sync: another tab modified the wishlist — re-sync this tab
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    _cache = null;
    updateHeaderCount();
    renderWishlistPage();
    document.querySelectorAll('.wishlist-btn').forEach(function (btn) {
      updateButton(btn, Wishlist.has(btn.dataset.productId));
    });
  });

  window.WishlistManager = { init: initButtons };

})();
